import React, { useEffect, useMemo, useState, useRef } from 'react';
import { IconButton, Paper, Divider, Tooltip } from '@mui/material';
import { ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, CropFree as CropFreeIcon, CropSquare as CropSquareIcon } from '@mui/icons-material';
import { getRunInfo } from '@services/runApi.service';
import type { RunAnalysisEntity, RunEntity } from '@/types/run-stage.types';
import { useRunAnalysisStore } from '@/run-stage/store/runAnalysisStore';
import { useGraphEditorStore, wireGraphToStore, type GraphComment } from '@/graph-store';
import { GraphEditorProvider, GraphEffects } from '@/graph-react';
import type { GraphSnapshot } from '@/graph';
import { deriveBatches, filterSnapshotByClone, resolveActiveClone } from '@/graph';
import { HighlightOverlays } from '@/graph-react/components/HighlightOverlays';
import { BatchSelector } from '@components/GraphViewer/BatchSelector';
import '../styles/RunAnalysisGraph.css';

interface RunAnalysisGraphProps {
  analysis: RunAnalysisEntity;
}

function toRunEntity(run: RunAnalysisEntity['run']): RunEntity | null {
  if (!run || typeof run === 'string') return null;
  return run;
}

function resolveRunId(analysis: RunAnalysisEntity): string | null {
  if (analysis.runId) return analysis.runId;
  if (typeof analysis.run === 'string') return analysis.run;
  if (analysis.run?._id) return analysis.run._id;
  return null;
}

/**
 * RunAnalysisGraph
 *
 * Renders the analysis graph snapshot in read-only mode using GraphEditorProvider.
 * Pattern matches WetlabWorkflow:
 *   1. GraphEditorProvider initializes the graph with the snapshot
 *   2. onReady callback: wireGraphToStore connects graph instance to store for zoom/pan
 *   3. GraphEffects wires graph events (selection) back to store
 *   4. HighlightOverlays shows visual feedback for selected nodes
 *   5. GraphFloatingControls provides zoom buttons
 */
export const RunAnalysisGraph: React.FC<RunAnalysisGraphProps> = ({ analysis }) => {
  const { setSelectedNode } = useRunAnalysisStore();
  const { setSelection } = useGraphEditorStore();
  const setGraphComments = useGraphEditorStore((s) => s.setGraphComments);
  const graphRef = useRef<any>(null);

  // ── Batch instance selection ──────────────────────────────────────────────
  // Batched runs clone the whole graph per instance with colliding ids and
  // identical positions, so only one instance can be rendered at a time.
  // Selection lives in the store because the protocol sidebar follows it too.
  const selectedCloneNumber = useRunAnalysisStore((s) => s.selectedCloneNumber);
  const setSelectedCloneNumber = useRunAnalysisStore((s) => s.setSelectedCloneNumber);

  const fullSnapshot = analysis.graphSnapshot as GraphSnapshot | undefined;
  const resolvedRunId = resolveRunId(analysis);
  const [runEntity, setRunEntity] = useState<RunEntity | null>(toRunEntity(analysis.run));

  useEffect(() => {
    setRunEntity(toRunEntity(analysis.run));

    if (!resolvedRunId) return;

    let cancelled = false;

    void getRunInfo(resolvedRunId)
      .then((runInfo) => {
        if (!cancelled) {
          setRunEntity(runInfo.entity);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('[RunAnalysisGraph] Failed to load run metadata:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [analysis.run, resolvedRunId]);

  useEffect(() => {
    const comments = ((analysis.allComments as any[]) || []).map((comment: any, index: number): GraphComment => {
      const rawTimestamp = comment.timestamp ?? comment.createdAt ?? comment.updatedAt;
      const numericTimestamp =
        typeof rawTimestamp === 'number'
          ? rawTimestamp
          : new Date(String(rawTimestamp ?? '')).getTime();

      return {
        _id: comment._id,
        id: comment.id || comment._id || `analysis-comment-${index}`,
        comment: comment.comment,
        entity: comment.entity,
        commentId: comment.commentId,
        new: comment.new,
        data: comment.data || comment.text || comment.comment || '',
        stepId: comment.stepId,
        addedBy: comment.addedBy || comment.user || { name: 'Unknown User' },
        timestamp: Number.isFinite(numericTimestamp) ? numericTimestamp : Date.now(),
        type: 'GRAPH',
      };
    });

    setGraphComments(comments);
  }, [analysis.allComments, setGraphComments]);

  const batches = useMemo(
    () => deriveBatches(fullSnapshot, runEntity?.batchedNodeId),
    [fullSnapshot, runEntity?.batchedNodeId],
  );

  // Resolved during render (not synced via an effect) so the first paint is
  // already narrowed. The sidebar resolves the same way from the same store
  // value, so the two stay in step without either owning the default.
  const cloneNumber = useMemo(
    () => resolveActiveClone(fullSnapshot, selectedCloneNumber),
    [fullSnapshot, selectedCloneNumber],
  );

  const viewSnapshot = useMemo(() => {
    if (!fullSnapshot) return undefined;
    if (cloneNumber == null) return fullSnapshot;
    return filterSnapshotByClone(fullSnapshot, cloneNumber);
  }, [fullSnapshot, cloneNumber]);

  // Get zoom actions and selection from graph editor store
  const zoomIn = useGraphEditorStore((s) => s.zoomIn);
  const zoomOut = useGraphEditorStore((s) => s.zoomOut);
  const zoomActual = useGraphEditorStore((s) => s.zoomActual);
  const zoomFit = useGraphEditorStore((s) => s.zoomFit);

  // Get graph editor store selection (one-way sync: graph → analysis store)
  const graphEditorSelection = useGraphEditorStore((s) => s.selection.cellIds);

  // Sync graph editor store selection to run analysis store
  // (when user clicks in graph). This makes protocol tab + info tab update.
  // For edge cells, extract the operation ID and select all related edges for TRANSFER operations
  useEffect(() => {
    // Ensure graphEditorSelection is an array
    if (!Array.isArray(graphEditorSelection)) {
      setSelectedNode(null);
      return;
    }
    
    if (graphEditorSelection.length > 0) {
      let nodeId = graphEditorSelection[0];
      let operationId: string | null = null;
      let shouldUpdateSelection = false;
      let newCellIds: string[] = [];
      
      // If it's an edge cell ID (op-edge-*), extract the operation ID
      if (typeof nodeId === 'string' && nodeId.startsWith('op-edge-')) {
        // Handle named edge variants: op-edge-${opId}-transfer, -remainder, -dest
        // Extract the base operation ID by removing 'op-edge-' prefix and any suffix
        let baseId = nodeId.replace('op-edge-', '');
        
        // Remove known suffixes to get the operation ID
        baseId = baseId.replace('-transfer', '').replace('-remainder', '').replace('-dest', '');
        
        operationId = baseId;
        nodeId = operationId;
        
        // For TRANSFER operations, select all related edges via store
        // Check if we have the graph instance stored in the ref
        if (graphRef.current && operationId) {
          const model = graphRef.current.getDataModel();
          const edgeIds: string[] = [
            `op-edge-${operationId}`,
            `op-edge-${operationId}-transfer`,
            `op-edge-${operationId}-remainder`,
            `op-edge-${operationId}-dest`,
          ];
          
          // Filter to only edges that actually exist in the graph
          const existingEdges = edgeIds.filter(id => {
            const cell = model.getCell(id);
            return cell && cell.isEdge?.();
          });
          
          // If multiple edges exist for this operation, check if we need to update selection
          if (existingEdges.length > 1) {
            // Only update if current selection is not already the multi-edge selection
            const isAlreadyMultiSelected = 
              Array.isArray(graphEditorSelection) && 
              graphEditorSelection.length === existingEdges.length &&
              existingEdges.every(id => graphEditorSelection.includes(id));
            
            if (!isAlreadyMultiSelected) {
              shouldUpdateSelection = true;
              newCellIds = existingEdges;
            }
          }
        }
      }

      // Only update selection if needed to avoid loops
      if (shouldUpdateSelection) {
        // Pass cellIds directly as an array, not as an object
        setSelection(newCellIds);
      }

      setSelectedNode(nodeId);
    } else {
      setSelectedNode(null);
    }
  }, [graphEditorSelection, setSelectedNode, setSelection]);

  // When graph is ready, wire it to store and seed store state
  const handleGraphReady = (graph: any) => {
    // Store graph instance in ref for use in effects
    graphRef.current = graph;
    
    // Connect graph instance to store so zoom/pan actions work
    wireGraphToStore(graph);
    
    // Seed store with the same snapshot the canvas was hydrated from — if the
    // two disagree about which cells exist, selection and cycle brackets break.
    useGraphEditorStore.getState().seedStoreState(viewSnapshot as GraphSnapshot);
    
    // Enable node selection by clicking (make sure graph is interactive for selection)
    graph.setCellsSelectable(true);
  };

  return (
    <div className="run-analysis-graph">
      <BatchSelector
        batches={batches}
        value={cloneNumber}
        onChange={setSelectedCloneNumber}
      />
      <GraphEditorProvider
        // cloneNumber is part of the key so switching batches remounts and
        // re-hydrates rather than layering instances onto each other.
        key={`${analysis._id}:${cloneNumber ?? 'all'}`}
        config={{ readOnly: false, disableContextMenu: true }}
        initialSnapshot={viewSnapshot as GraphSnapshot}
        onReady={handleGraphReady}
        containerClassName="graph-canvas-scroll"
        containerStyle={{
          position: 'absolute',
          inset: 0,
          overflow: 'auto',
          padding: '20px',
        }}
      >
        {/* Wire graph events (selection, undo/redo, etc.) back to store */}
        <GraphEffects />
        
        {/* Show visual highlight for selected node/edge */}
        <HighlightOverlays />
      </GraphEditorProvider>
      
      {/* Floating zoom controls */}
      <Paper
        elevation={2}
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 1,
          overflow: 'hidden',
          zIndex: 10,
        }}
      >
        <Tooltip title="Zoom In" placement="left">
          <IconButton size="small" onClick={zoomIn} sx={{ borderRadius: 0 }}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Zoom Out" placement="left">
          <IconButton size="small" onClick={zoomOut} sx={{ borderRadius: 0 }}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Fit to Window" placement="left">
          <IconButton size="small" onClick={zoomFit} sx={{ borderRadius: 0 }}>
            <CropFreeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem />
        <Tooltip title="Actual Size" placement="left">
          <IconButton size="small" onClick={zoomActual} sx={{ borderRadius: 0 }}>
            <CropSquareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </div>
  );
};
